import React from "react";
import { IcArrow, IcBack, IcBuilding, IcCheck, IcDoc, IcHome, IcShield, IcX } from "./shared.jsx";
import { api, idempotencyKey } from "../lib/api.js";
import { agreementsGate } from "../lib/agreements-gate.js";
// Step components for the onboarding wizard
const { useState: uS, useEffect: uE, useRef: uR, useMemo: uM } = React;

// ============ WELCOME ============
function StepWelcome({ onNext, agent }) {
  return (
    <div style={{maxWidth: 880}}>
      <div className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>Welcome Aboard</div>
      <h1 style={{font: "300 clamp(52px, 7vw, 96px)/0.98 var(--ep-font-display)", letterSpacing: "-0.02em", margin: "12px 0 28px"}}>
        Welcome to<br/>EveryPlace, {agent.name.split(" ")[0]}.
      </h1>
      <p style={{font: "400 19px/1.55 var(--ep-font-body)", color: "var(--ep-fg-muted)", maxWidth: "52ch", marginBottom: 40}}>
        We'll walk you through joining the brokerage at the pace the decisions require. Four steps — clear, steady, handled properly.
      </p>

      <div style={{display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, background: "var(--ep-border)", border: "1px solid var(--ep-border)", marginBottom: 48}}>
        {[
          {n: "01", t: "Confirm your profile", d: "License, contact info, team structure."},
          {n: "02", t: "Review & sign agreements", d: "EveryPlace IC, Laughton Team, addenda."},
          {n: "03", t: "Transfer your listings", d: "MLS pull + broker & owner signatures."},
          {n: "04", t: "Transfer your license", d: "Arizona Department of Real Estate."},
        ].map(s => (
          <div key={s.n} style={{background: "var(--ep-bg-elevated)", padding: "22px 20px", display: "flex", flexDirection: "column", gap: 10, minHeight: 180}}>
            <div className="ep-caption" style={{color: "var(--ep-sunset)"}}>{s.n}</div>
            <div style={{font: "300 22px/1.15 var(--ep-font-display)", letterSpacing: "-0.01em"}}>{s.t}</div>
            <div style={{font: "400 13px/1.5 var(--ep-font-body)", color: "var(--ep-fg-muted)"}}>{s.d}</div>
          </div>
        ))}
      </div>

      <div style={{display: "flex", alignItems: "center", gap: 20, padding: "24px 28px", background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", borderRadius: 2}}>
        <div style={{flex: 1}}>
          <div style={{font: "400 13px/1.5 var(--ep-font-body)", color: "rgba(255,245,228,0.8)"}}>
            Save as you go. Everything here is stored to your agent file.
          </div>
        </div>
        <button className="ep-btn primary" onClick={onNext}>Begin Onboarding <IcArrow/></button>
      </div>
    </div>
  );
}

// ============ PROFILE ============
function StepProfile({ onNext, onBack, agent, setAgent }) {
  const [confirmed, setConfirmed] = uS(false);
  const [saving, setSaving] = uS(false);

  async function handleNext() {
    setSaving(true);
    try {
      await api.patch("/v1/profile", { phone: agent.phone, email: agent.email, nrdsId: agent.nrdsId });
    } catch (_) { /* non-blocking — continue even if save fails */ }
    setSaving(false);
    onNext();
  }

  // New-agent (intake pipeline) recruits often have no license, MLS id, NRDS,
  // or current brokerage yet — those get collected in the Welcome form or
  // enriched later from the MLS. Read-only identity fields render only when
  // there's a value to confirm; copy adjusts to the recruit context.
  const recruit = !!agent.recruit;
  const RO = ({ label, value }) => value ? (
    <div className="ep-field">
      <span className="ep-eyebrow">{label}</span>
      <div className="ep-input" style={{background: "var(--ep-origin-10)", color: "var(--ep-fg-muted)", cursor: "default"}}>{value}</div>
    </div>
  ) : null;

  return (
    <div style={{maxWidth: 960}}>
      <StepHeader eyebrow="Your Profile" title="Confirm your profile."
        subtitle={recruit
          ? "Confirm your contact details — we'll use these for your agreements and team setup. Licensing details come later in your Welcome form."
          : "Review your information below. Update your email, mobile, or NRDS # if anything has changed."}/>

      <div className="ep-grid-2" style={{gap: 24, marginBottom: 40}}>
        <RO label="Legal Name" value={agent.name}/>
        <RO label="ADRE License #" value={agent.license}/>
        <label className="ep-field">
          <span className="ep-eyebrow">Email</span>
          <input className="ep-input" value={agent.email} onChange={e => setAgent({...agent, email: e.target.value})}/>
        </label>
        <label className="ep-field">
          <span className="ep-eyebrow">Mobile</span>
          <input className="ep-input" value={agent.phone} onChange={e => setAgent({...agent, phone: e.target.value})}/>
        </label>
        <RO label="ARMLS Agent ID" value={agent.shortId}/>
        {(!recruit || agent.nrdsId) && (
          <label className="ep-field">
            <span className="ep-eyebrow">NRDS #</span>
            <input className="ep-input" value={agent.nrdsId || ""} placeholder="NAR member number"
              onChange={e => setAgent({...agent, nrdsId: e.target.value})}/>
            <span style={{fontSize: 11, color: "var(--ep-fg-muted)", marginTop: 4}}>
              Don't know your NRDS #?{" "}
              <a href="https://login.connect.realtor/#!/forgotmember" target="_blank" rel="noopener noreferrer"
                 style={{color: "var(--ep-sunset)"}}>Look it up →</a>
            </span>
          </label>
        )}
        <RO label="Current Broker of Record" value={agent.currentBroker}/>
      </div>

      <label style={{display: "flex", alignItems: "flex-start", gap: 10, margin: "28px 0 32px", cursor: "pointer", maxWidth: "62ch"}}>
        <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{marginTop: 3, accentColor: "var(--ep-sunset)", width: 16, height: 16}}/>
        <span style={{fontSize: 14, color: "var(--ep-fg)"}}>
          {recruit
            ? "I confirm this information is accurate. EveryPlace will use it to prepare my agreements in the next step."
            : "I confirm this information is accurate and matches my ADRE record. EveryPlace will use it to prepare the agreements in the next step."}
        </span>
      </label>

      <FooterNav onBack={onBack} onNext={handleNext} nextDisabled={!confirmed || saving}
        nextLabel={saving ? "Saving…" : undefined}/>
    </div>
  );
}

// ============ AGREEMENTS ============
// Two separate BoldSign packages + W-9. All three required to advance.
//   1. EveryPlace Agreements — ICA + Schedules + Broker Policy (Amber counter-signs)
//   2. Laughton Team Amendments — Founding Member + Team Agreement (Michelle counter-signs)
//   3. W-9 Tax Form — agent self-signs
function StepAgreements({ onNext, onBack, agreements, agent, gate, serverDocs, onAwaitingSignature, onEveryplaceSigned, onLaughtonSigned , eyebrow}) {
  // New-agent recruits (intake pipeline) sign the standalone ICA — role 1
  // Agent Services (auto-signs), role 2 the agent. Transfer-day agents sign
  // the original three-package set. The gate listens for the ica doc type.
  const recruit = !!agent?.recruit;
  // All agents are Founding Agents for now — update after initial launch period.
  const isFoundingAgent = !recruit;

  // EveryPlace package — persists via agreements prop across navigation.
  // Trust the SERVER for a recruit's ICA, not just this session's memory.
  // `agreements` is ephemeral React state (useState({}) in App.jsx, never
  // hydrated from the API), so a page reload dropped it entirely — leaving a
  // recruit who had genuinely signed looking at an unsigned ICA with "skip" as
  // the only way forward, while signed_documents said 'completed'. And since
  // this same flag drives the signing card below, the stale version also still
  // offered a Sign button, so continuing that way would have sent a SECOND ICA.
  // /onboarding-gate/status.icaDone is server-owned and survives reloads.
  const epSigned = !!agreements.everyplace?.signed || (recruit && !!gate?.icaDone);
  const epWhen   = agreements.everyplace?.when;
  const [ep, setEp] = uS({ status: "idle", error: null });

  // Laughton amendments — persists via agreements prop.
  const ltSigned = !!agreements.laughton?.signed;
  const ltWhen   = agreements.laughton?.when;
  const [lt, setLt] = uS({ status: "idle", error: null });

  // W-9 — local state only (self-reported).
  const [w9, setW9] = uS({ status: "idle", warn: false, error: null });
  // Was local-only, so a reload lost it and a transfer-day agent had to re-send
  // their W-9 — a second real envelope for a form already on file. The server
  // has tracked w9 as its own doc_type all along; it just was not consulted.
  const w9Done = w9.status === "done" || !!serverDocs?.w9?.signed;

  // Which server doc_type backs each card. Recruits sign the standalone ICA;
  // transfer-day agents sign the bundled package.
  const epDocType  = recruit ? "ica" : "everyplace_package";
  const epAwaiting = !epSigned && !!serverDocs?.[epDocType]?.sent;
  const ltAwaiting = !ltSigned && !!serverDocs?.laughton_amendments?.sent;
  const sentOn = (d) => d?.sentAt
    ? new Date(d.sentAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  // ONE rule, one place. canContinue and the footer label used to be separate
  // expressions of this and drifted — see lib/agreements-gate.js for what that
  // cost. Never reintroduce a second copy of this logic.
  const gateState = agreementsGate({ recruit, alreadySigned: false, epSigned, ltSigned, w9Done });
  const canContinue = gateState.ok;

  async function signEveryplace() {
    setEp(s => ({ ...s, status: "sending", error: null }));
    try {
      const res = await api.post(recruit ? "/v1/documents/ica" : "/v1/documents/everyplace",
        {}, { headers: { "Idempotency-Key": idempotencyKey() } });
      if (res.signUrl) window.open(res.signUrl, "_blank", "noopener");
      setEp(s => ({ ...s, status: "signing" }));
      // Start polling the gate NOW. Signing happens in a separate BoldSign tab,
      // so this window gets no event when it finishes — without this the page
      // sits on "Sign in the new tab" forever even though the webhook has
      // already landed and the server knows. The in-app modal path calls this
      // via handleSigned; this path had no equivalent, which is the whole reason
      // a genuinely-signed ICA still read as unsigned.
      onAwaitingSignature && onAwaitingSignature();
    } catch (e) { setEp(s => ({ ...s, status: "error", error: e.message || "Could not open signing link" })); }
  }

  function confirmEveryplace() {
    const when = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    setEp(s => ({ ...s, status: "done" }));
    onEveryplaceSigned && onEveryplaceSigned(when);
  }

  async function signLaughton() {
    setLt(s => ({ ...s, status: "sending", error: null }));
    try {
      const res = await api.post("/v1/documents/laughton", {}, { headers: { "Idempotency-Key": idempotencyKey() } });
      if (res.signUrl) window.open(res.signUrl, "_blank", "noopener");
      setLt(s => ({ ...s, status: "signing" }));
    } catch (e) { setLt(s => ({ ...s, status: "error", error: e.message || "Could not open signing link" })); }
  }

  function confirmLaughton() {
    const when = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    setLt(s => ({ ...s, status: "done" }));
    onLaughtonSigned && onLaughtonSigned(when);
  }

  async function submitW9() {
    setW9(s => ({ ...s, status: "sending", warn: false, error: null }));
    try {
      const res = await api.post("/v1/documents/w9", {}, { headers: { "Idempotency-Key": idempotencyKey() } });
      if (res.signUrl) window.open(res.signUrl, "_blank", "noopener");
      setW9(s => ({ ...s, status: "signing" }));
    } catch (e) { setW9(s => ({ ...s, status: "error", error: e.message || "Could not open W-9" })); }
  }

  const epDocs = recruit ? [
    { icon: <IcBuilding size={18}/>, title: "Independent Contractor Agreement (ICA)",
      sub: "Your agreement with The Laughton Team at EveryPlace — countersigned by Agent Services." },
  ] : [
    { icon: <IcBuilding size={18}/>, title: "Independent Contractor Agreement (ICA)" },
    { icon: <IcDoc size={18}/>,      title: "Schedule A — State Definition" },
    { icon: <IcDoc size={18}/>,      title: "Schedule B — Fee Schedule",
      sub: isFoundingAgent ? "$35/mo · $100/transaction · E&O waived through 2026" : null },
    { icon: <IcDoc size={18}/>,      title: "Broker Policy Manual Acknowledgment" },
    { icon: <IcDoc size={18}/>,      title: "Broker Policy Manual" },
  ];

  const ltDocs = [
    { icon: <IcShield size={18}/>, title: "Founding Member Addendum",
      sub: isFoundingAgent ? "Locks in your Founding Agent rates as a permanent addendum." : null },
    { icon: <IcHome size={18}/>,   title: "Laughton Team Agreement Addendum",
      sub: "Team operations, splits, shared resources, and performance expectations." },
  ];

  const foundingTerms = [
    { label: "No Sign-Up Fees",  value: "All onboarding and sign-up fees waived for Founding Members." },
    { label: "Monthly Fee",      value: "Waived Jul–Sep · $35/mo starting Oct 1" },
    { label: "Transaction Fee",  value: "$100 per closed transaction through 2026 (includes transferred escrows)" },
    { label: "E&O Insurance",    value: "Waived through end of 2026" },
    { label: "Business Cards",   value: "First order of EveryPlace business cards on us" },
  ];

  return (
    <div style={{maxWidth: 960}}>
      {/* A recruit signs exactly one document, so the transfer-day copy about a
          bundled multi-document package described something they cannot see. */}
      <StepHeader eyebrow={eyebrow || "Step 03 / 09"} title="Review & sign your agreements."
        subtitle={recruit
          ? "Your Independent Contractor Agreement — counter-signed for you, so one signature completes it."
          : "All documents are bundled into a single signing package — one session covers everything."}/>

      {isFoundingAgent && (
        <div style={{marginBottom: 32, border: "1px solid var(--ep-sunset)", borderRadius: 2, overflow: "hidden"}}>
          {/* Header */}
          <div style={{display: "flex", alignItems: "center", gap: 14, padding: "16px 22px",
            background: "var(--ep-sunset)", color: "var(--ep-saguaro-blossom)"}}>
            <IcShield size={20}/>
            <div>
              <div style={{font: "600 14px/1.2 var(--ep-font-body)", letterSpacing: "0.02em"}}>FOUNDING AGENT</div>
              <div style={{font: "300 20px/1.2 var(--ep-font-display)", marginTop: 2}}>Your locked-in terms — review before signing.</div>
            </div>
          </div>
          {/* Terms grid */}
          <div style={{background: "#FFFAF0"}}>
            {foundingTerms.map((t, i) => (
              <div key={t.label} style={{display: "grid", gridTemplateColumns: "160px 1fr", gap: 16,
                padding: "14px 22px", borderBottom: i < foundingTerms.length - 1 ? "1px solid var(--ep-divider)" : 0}}>
                <div style={{fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600,
                  color: "var(--ep-fg-muted)", paddingTop: 2}}>{t.label}</div>
                <div style={{fontSize: 14, color: "var(--ep-origin)", fontWeight: 500, lineHeight: 1.45}}>{t.value}</div>
              </div>
            ))}
          </div>
          <div style={{padding: "10px 22px", background: "rgba(255,157,58,0.06)", borderTop: "1px solid var(--ep-divider)",
            fontSize: 12, color: "var(--ep-fg-muted)"}}>
            These terms are memorialized in the Fee Schedule Addendum and Founding Member Addendum.
          </div>
        </div>
      )}

      {/* ── SECTION 1: EveryPlace Agreements (recruits: standalone ICA) ── */}
      <AgreementSection
        eyebrow={recruit ? "Your Agreement" : "EveryPlace Agreements"}
        title={recruit ? "Independent Contractor Agreement" : "EveryPlace Independent Contractor Package"}
        docs={epDocs}
        signed={epSigned}
        signedWhen={epWhen}
        signingStatus={ep.status}
        signingError={ep.error}
        counterSigner={recruit ? "Agent Services, The Laughton Team" : "Amber Valentine, Designated Broker"}
        awaiting={epAwaiting}
        awaitingSince={sentOn(serverDocs?.[epDocType])}
        onSign={signEveryplace}
        onConfirm={confirmEveryplace}
      />

      {/* ── SECTION 2: Laughton Team Amendments (transfer-day only) ── */}
      {!recruit && <AgreementSection
        eyebrow="Laughton Team Amendments"
        title="Team Agreement Package"
        docs={ltDocs}
        signed={ltSigned}
        signedWhen={ltWhen}
        signingStatus={lt.status}
        signingError={lt.error}
        counterSigner="Michelle Bleach, Team Lead Director"
        awaiting={ltAwaiting}
        awaitingSince={sentOn(serverDocs?.laughton_amendments)}
        onSign={signLaughton}
        onConfirm={confirmLaughton}
      />}

      {/* ── SECTION 3: W-9 (transfer-day only) ── */}
      {!recruit && <div style={{marginBottom: 32}}>
        <div className="ep-eyebrow" style={{color: "var(--ep-sunset)", marginBottom: 12}}>Tax Form — Required Separately</div>
        <div className="ep-card" style={{padding: 22, display: "flex", alignItems: "center", gap: 18,
          borderColor: w9Done ? "var(--ep-avocado)" : "var(--ep-border)",
          background: w9Done ? "rgba(204,207,129,0.1)" : "var(--ep-bg-elevated)"}}>
          <div style={{width: 40, height: 40, borderRadius: 2,
            background: w9Done ? "var(--ep-avocado)" : "var(--ep-origin-10)",
            display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ep-origin)", flexShrink: 0}}>
            {w9Done ? <IcCheck size={20}/> : <IcDoc size={20}/>}
          </div>
          <div style={{flex: 1, minWidth: 0}}>
            <div style={{font: "300 20px/1.2 var(--ep-font-display)", letterSpacing: "-0.005em", marginBottom: 3}}>W-9 Tax Form</div>
            <div style={{font: "400 13px/1.5 var(--ep-font-body)", color: "var(--ep-fg-muted)"}}>
              Required for EveryPlace to process commission payments to you.
            </div>
          </div>
          <div style={{flexShrink: 0}}>
            {w9Done ? (
              <span className="ep-badge signed"><IcCheck size={10}/> Submitted</span>
            ) : w9.status === "signing" ? (
              <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8}}>
                <span style={{fontSize: 12, color: "var(--ep-fg-muted)"}}>Complete the form in the new tab, then return here</span>
                <button className="ep-btn primary sm" onClick={() => setW9(s => ({...s, status: "done"}))}>
                  <IcCheck size={12}/> I've completed my W-9
                </button>
              </div>
            ) : (
              <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4}}>
                <button className="ep-btn primary sm" onClick={() => setW9(s => ({...s, warn: true}))}
                  disabled={w9.status === "sending"}>
                  {w9.status === "sending" ? "Opening…" : <>Sign W-9 <IcArrow size={12}/></>}
                </button>
                {w9.status === "error" && <span style={{color: "var(--ep-rosewood)", fontSize: 11}}>{w9.error}</span>}
              </div>
            )}
          </div>
        </div>
      </div>}

      {/* W-9 warning modal */}
      {w9.warn && (
        <div className="modal-backdrop" onClick={e => e.target.classList.contains("modal-backdrop") && setW9(s => ({...s, warn: false}))}>
          <div style={{width: "min(500px, 94vw)", background: "var(--ep-bg-elevated)", borderRadius: 4,
            padding: 36, animation: "slideUp 240ms var(--ep-ease)", display: "flex", flexDirection: "column", gap: 20}}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16}}>
              <div style={{font: "300 28px/1.1 var(--ep-font-display)"}}>Before you sign your W-9</div>
              <button className="ep-btn ghost sm" onClick={() => setW9(s => ({...s, warn: false}))} style={{flexShrink: 0}}><IcX size={16}/></button>
            </div>
            <div style={{padding: "16px 18px", background: "rgba(255,157,58,0.12)",
              border: "1px solid var(--ep-high-noon)", borderRadius: 2, lineHeight: 1.65}}>
              <div style={{fontWeight: 600, color: "var(--ep-origin)", marginBottom: 10, fontSize: 14}}>
                Fill this out with the information you used to file your last tax return.
              </div>
              <div style={{display: "flex", flexDirection: "column", gap: 8, fontSize: 14, color: "var(--ep-fg)"}}>
                <div>· Your <strong>full legal name</strong>, address, and punctuation must match exactly</div>
                <div>· <strong>Double-check your SSN or EIN</strong> is correct before submitting</div>
              </div>
            </div>
            <div style={{fontSize: 13, color: "var(--ep-fg-muted)", lineHeight: 1.55}}>
              The W-9 will open in a new tab. Complete and submit it there, then return here to confirm.
            </div>
            <div style={{display: "flex", justifyContent: "flex-end", gap: 10}}>
              <button className="ep-btn ghost" onClick={() => setW9(s => ({...s, warn: false}))}>Cancel</button>
              <button className="ep-btn primary" onClick={submitW9}>I Understand — Open W-9 <IcArrow size={12}/></button>
            </div>
          </div>
        </div>
      )}

      {/* No skip affordance. The ICA is the agreement that makes someone an
          EveryPlace agent — advancing without it produced an agent whose record
          said onboarded while no executed contract existed. The signed state now
          comes from the server (gate.icaDone / serverDocs), so an agent who
          genuinely signed is recognised without needing to assert it. */}
      <FooterNav onBack={onBack} onNext={onNext} nextDisabled={!canContinue}
        nextLabel={gateState.label}
      />
    </div>
  );
}

// Reusable signing section used for both EveryPlace and Laughton packages.
// `awaiting` = the server has an envelope for this doc_type at status 'sent':
// BoldSign holds it and has emailed the signer, but the completion webhook has
// not landed yet (~5 minutes in practice). Without this state the card showed a
// plain Sign button, whose onSign mints a NEW Idempotency-Key and therefore a
// SECOND real envelope for a document already out for signature. So when we know
// one is already pending, the primary action becomes "I've completed signing" and
// re-sending is demoted to a deliberate, clearly-labelled secondary.
function AgreementSection({ eyebrow, title, docs, signed, signedWhen, signingStatus, signingError, counterSigner, awaiting, awaitingSince, onSign, onConfirm }) {
  return (
    <div style={{marginBottom: 40}}>
      <div className="ep-eyebrow" style={{color: "var(--ep-sunset)", marginBottom: 12}}>{eyebrow}</div>

      {/* Document list */}
      <div className="vstack" style={{gap: 10, marginBottom: 20}}>
        {docs.map((d, i) => (
          <div key={i} className="ep-card" style={{padding: "16px 20px", display: "flex", alignItems: "center", gap: 16,
            borderColor: signed ? "var(--ep-avocado)" : "var(--ep-border)",
            background: signed ? "rgba(204,207,129,0.1)" : "var(--ep-bg-elevated)"}}>
            <div style={{width: 36, height: 36, borderRadius: 2,
              background: signed ? "var(--ep-avocado)" : "var(--ep-origin-10)",
              display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ep-origin)", flexShrink: 0}}>
              {signed ? <IcCheck size={18}/> : d.icon}
            </div>
            <div style={{flex: 1, minWidth: 0}}>
              <div style={{font: "300 17px/1.2 var(--ep-font-display)", letterSpacing: "-0.005em"}}>{d.title}</div>
              {d.sub && <div style={{fontSize: 12, color: "var(--ep-fg-muted)", marginTop: 3}}>{d.sub}</div>}
            </div>
            {signed && <span className="ep-badge signed"><IcCheck size={10}/> Signed</span>}
          </div>
        ))}
      </div>

      {/* Sign / status banner */}
      <div style={{padding: "18px 22px", background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", borderRadius: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap"}}>
        <div>
          <div style={{font: "300 18px/1.2 var(--ep-font-display)", marginBottom: 4}}>
            {/* signedWhen is absent whenever `signed` came from the server
                rather than this session's signing modal — the gate reports
                icaDone without a date, which rendered the literal string
                "Signed undefined." Never interpolate it unguarded. */}
            {signed ? (signedWhen ? `Signed ${signedWhen}.` : "Signed.")
              : awaiting ? `Sent${awaitingSince ? ` ${awaitingSince}` : ""} · awaiting your signature.`
              : `${docs.length} document${docs.length > 1 ? "s" : ""} · Sign once.`}
          </div>
          <div style={{fontSize: 12, color: "rgba(255,245,228,0.75)"}}>
            {signed
              ? `Counter-signed by ${counterSigner} and filed.`
              : awaiting
                ? `Check your email for the signing link. Counter-signed by ${counterSigner}.`
                : `Counter-signed by ${counterSigner}.`}
          </div>
        </div>
        <div style={{flexShrink: 0}}>
          {signed ? (
            <span className="ep-badge signed" style={{background: "rgba(204,207,129,0.3)", borderColor: "rgba(204,207,129,0.5)"}}>
              <IcCheck size={10}/> Complete
            </span>
          ) : signingStatus === "signing" ? (
            <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8}}>
              <span style={{fontSize: 12, color: "rgba(255,245,228,0.8)"}}>Sign in the new tab, then return here</span>
              <button className="ep-btn primary sm" style={{background: "var(--ep-high-noon)", color: "var(--ep-origin)"}}
                onClick={onConfirm}>
                <IcCheck size={12}/> I've completed signing
              </button>
            </div>
          ) : awaiting ? (
            // An envelope is already out. Confirming is the primary path;
            // re-sending is possible but must be an explicit choice, since it
            // creates another real document for the signer.
            <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6}}>
              <button className="ep-btn primary sm" style={{background: "var(--ep-high-noon)", color: "var(--ep-origin)"}}
                onClick={onConfirm}>
                <IcCheck size={12}/> I've completed signing
              </button>
              <button className="ep-btn ghost sm" style={{color: "rgba(255,245,228,0.75)"}}
                onClick={onSign} disabled={signingStatus === "sending"}>
                {signingStatus === "sending" ? "Sending…" : "Didn't get it? Send a new copy"}
              </button>
              {signingError && <span style={{fontSize: 11, color: "var(--ep-high-noon)"}}>{signingError}</span>}
            </div>
          ) : (
            <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4}}>
              <button className="ep-btn primary sm" style={{background: "var(--ep-high-noon)", color: "var(--ep-origin)"}}
                onClick={onSign} disabled={signingStatus === "sending"}>
                {signingStatus === "sending" ? "Opening…" : <>Sign {eyebrow.split(" ")[0]} Agreements <IcArrow size={12}/></>}
              </button>
              {signingError && <span style={{fontSize: 11, color: "var(--ep-high-noon)"}}>{signingError}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ HEADER / FOOTER PRIMITIVES ============
function StepHeader({ eyebrow, title, subtitle }) {
  return (
    <header style={{marginBottom: 36, maxWidth: 780}}>
      <div className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>{eyebrow}</div>
      <h1 style={{font: "300 clamp(40px, 5vw, 64px)/1.02 var(--ep-font-display)", letterSpacing: "-0.015em", margin: "10px 0 16px", textWrap: "balance"}}>
        {title}
      </h1>
      {subtitle && <p style={{font: "400 17px/1.55 var(--ep-font-body)", color: "var(--ep-fg-muted)", maxWidth: "56ch", margin: 0}}>{subtitle}</p>}
    </header>
  );
}

function FooterNav({ onBack, onNext, nextDisabled, nextLabel = "Continue", backLabel = "Back", aboveNav }) {
  return (
    <div style={{paddingTop: 32, borderTop: "1px solid var(--ep-border)"}}>
      {aboveNav && aboveNav}
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        {onBack ? <button className="ep-btn ghost" onClick={onBack}><IcBack/> {backLabel}</button> : <span/>}
        <button className="ep-btn primary" onClick={onNext} disabled={nextDisabled}>{nextLabel} <IcArrow/></button>
      </div>
    </div>
  );
}

export { StepWelcome, StepProfile, StepAgreements, StepHeader, FooterNav };
